// ============================================
// Dispatcher Settings Page — Profile & Security
// Wrapped in DispatcherLayout for sidebar/header
// ============================================

import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import api from '../../services/api';
import DispatcherLayout from '../../components/dispatcher/DispatcherLayout';
import { loadUser } from '../../redux/slices/authSlice';

// Password strength checker
const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '' };
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
    if (score <= 2) return { score, label: 'Weak', color: 'bg-rose-500' };
    if (score <= 4) return { score, label: 'Fair', color: 'bg-amber-500' };
    if (score <= 5) return { score, label: 'Good', color: 'bg-emerald-400' };
    return { score, label: 'Strong', color: 'bg-emerald-600' };
};

const PasswordField = ({ label, value, onChange, placeholder }) => {
    const [show, setShow] = useState(false);
    return (
        <div className="space-y-3">
            <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-emerald-500" /> {label}
            </label>
            <div className="relative">
                <input
                    type={show ? 'text' : 'password'}
                    value={value || ''}
                    onChange={onChange}
                    placeholder={placeholder || ''}
                    className="w-full px-6 py-4 pr-14 rounded-2xl bg-white/80 dark:bg-slate-800/50 border-2 border-white/80 dark:border-white/10 focus:border-emerald-500 dark:focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/5 outline-none transition-all text-sm font-black text-slate-700 dark:text-white shadow-sm hover:border-slate-200 dark:hover:border-white/20"
                />
                <button
                    type="button"
                    onClick={() => setShow(s => !s)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
                >
                    {show ? (
                        <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" /></svg>
                    ) : (
                        <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    )}
                </button>
            </div>
        </div>
    );
};

const Settings = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);

    // Profile form
    const [formData, setFormData] = useState({ name: user?.name || '', email: user?.email || '', phone: user?.phone || '' });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Password change form
    const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [pwLoading, setPwLoading] = useState(false);
    const [pwErrors, setPwErrors] = useState({});
    const [pwMessage, setPwMessage] = useState({ type: '', text: '' });

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });
        try {
            await api.patch('/dispatcher/profile', formData);
            setMessage({ type: 'success', text: 'Profile updated successfully!' });
            dispatch(loadUser());
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Update failed' });
        } finally {
            setLoading(false);
        }
    };

    const validatePwForm = () => {
        const errs = {};
        if (!pwForm.currentPassword) errs.currentPassword = 'Current password is required';
        if (!pwForm.newPassword) errs.newPassword = 'New password is required';
        else if (pwForm.newPassword.length < 8) errs.newPassword = 'Must be at least 8 characters';
        else if (!/[A-Z]/.test(pwForm.newPassword)) errs.newPassword = 'Must include an uppercase letter';
        else if (!/[a-z]/.test(pwForm.newPassword)) errs.newPassword = 'Must include a lowercase letter';
        else if (!/[0-9]/.test(pwForm.newPassword)) errs.newPassword = 'Must include a number';
        else if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwForm.newPassword)) errs.newPassword = 'Must include a special character';
        if (!pwForm.confirmPassword) errs.confirmPassword = 'Please confirm your new password';
        else if (pwForm.newPassword !== pwForm.confirmPassword) errs.confirmPassword = 'Passwords do not match';
        if (pwForm.currentPassword && pwForm.newPassword && pwForm.currentPassword === pwForm.newPassword) {
            errs.newPassword = 'New password must be different from current password';
        }
        return errs;
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        const errs = validatePwForm();
        if (Object.keys(errs).length > 0) { setPwErrors(errs); return; }
        setPwErrors({});
        setPwLoading(true);
        setPwMessage({ type: '', text: '' });
        try {
            await api.put('/auth/change-password', { currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
            setPwMessage({ type: 'success', text: 'Password changed successfully! Keep it safe.' });
            setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        } catch (err) {
            setPwMessage({ type: 'error', text: err.response?.data?.message || 'Failed to change password. Please try again.' });
        } finally {
            setPwLoading(false);
        }
    };

    const strength = getPasswordStrength(pwForm.newPassword);

    const AlertBanner = ({ type, text, onClose }) => (
        <div className={`p-5 rounded-2xl text-xs font-black uppercase tracking-widest border ${type === 'success' ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-100 dark:border-emerald-500/20 text-emerald-700 dark:text-emerald-400' : 'bg-red-50 dark:bg-red-500/10 border-red-100 dark:border-red-500/20 text-red-700 dark:text-red-400'} shadow-sm`}>
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    {type === 'success' ? (
                        <svg className="w-5 h-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                    ) : (
                        <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                    )}
                    {text}
                </div>
                {onClose && <button onClick={onClose} className="opacity-60 hover:opacity-100 transition-opacity"><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg></button>}
            </div>
        </div>
    );

    return (
        <DispatcherLayout title="Settings" subtitle="Manage your dispatcher profile and security">
            <main className="flex-1 overflow-y-auto p-4 sm:p-8 bg-transparent sidebar-scroll transition-colors duration-500">
                <div className="max-w-2xl mx-auto space-y-8">

                    {/* ── Profile Card ── */}
                    <div className="relative bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-black/50 overflow-hidden group hover:shadow-2xl hover:shadow-emerald-500/20 dark:hover:shadow-emerald-500/10 transition-all duration-500">
                        <div className="p-8 sm:p-10 border-b border-white/60 dark:border-white/5 bg-white/40 dark:bg-slate-800/20 relative overflow-hidden transition-colors">
                            <div className="absolute -right-10 -top-10 w-32 h-32 bg-emerald-50 dark:bg-emerald-500/5 rounded-full opacity-40 group-hover:scale-150 transition-transform duration-700" />
                            <div className="flex flex-col sm:flex-row items-center gap-8 relative z-10">
                                <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-4xl font-black shadow-2xl shadow-emerald-500/30 transform group-hover:rotate-3 transition-transform">
                                    {user?.name?.charAt(0) || 'D'}
                                </div>
                                <div className="text-center sm:text-left">
                                    <h3 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">{user?.name}</h3>
                                    <p className="text-sm text-slate-400 dark:text-slate-500 font-bold uppercase tracking-widest">{user?.email}</p>
                                    <div className="mt-4 flex flex-wrap justify-center sm:justify-start gap-2">
                                        <span className="px-4 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[10px] font-black uppercase tracking-widest border border-emerald-100 dark:border-emerald-500/20 shadow-sm">
                                            Dispatcher Role
                                        </span>
                                        <span className="px-4 py-1.5 rounded-full bg-slate-900 dark:bg-white/10 text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-black/10">
                                            Active Status
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="p-8 sm:p-10 space-y-8">
                            {message.text && (
                                <AlertBanner type={message.type} text={message.text} onClose={() => setMessage({ type: '', text: '' })} />
                            )}

                            <div className="grid gap-8">
                                <div className="space-y-3">
                                    <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                        <span className="w-1 h-1 rounded-full bg-emerald-500" /> Display Name
                                    </label>
                                    <input
                                        name="name" type="text" value={formData.name} onChange={handleChange} required
                                        className="w-full px-6 py-4 rounded-2xl bg-white/80 dark:bg-slate-800/50 border-2 border-white/80 dark:border-white/10 focus:border-emerald-500 dark:focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/5 outline-none transition-all text-sm font-black text-slate-700 dark:text-white shadow-sm hover:border-slate-200 dark:hover:border-white/20"
                                    />
                                </div>

                                <div className="space-y-3">
                                    <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                        <span className="w-1 h-1 rounded-full bg-emerald-500" /> Email Address
                                    </label>
                                    <input
                                        name="email" type="email" value={formData.email} onChange={handleChange} required
                                        className="w-full px-6 py-4 rounded-2xl bg-white/80 dark:bg-slate-800/50 border-2 border-white/80 dark:border-white/10 focus:border-emerald-500 dark:focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/5 outline-none transition-all text-sm font-black text-slate-700 dark:text-white shadow-sm hover:border-slate-200 dark:hover:border-white/20"
                                    />
                                </div>
                                <div className="space-y-3">
                                    <label className="ml-1 uppercase tracking-[0.2em] text-[10px] font-black text-slate-400 flex items-center gap-2">
                                        <span className="w-1 h-1 rounded-full bg-emerald-500" /> Phone Number
                                    </label>
                                    <input
                                        name="phone" type="tel" value={formData.phone} onChange={handleChange}
                                        className="w-full px-6 py-4 rounded-2xl bg-white/80 dark:bg-slate-800/50 border-2 border-white/80 dark:border-white/10 focus:border-emerald-500 dark:focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/5 outline-none transition-all text-sm font-black text-slate-700 dark:text-white shadow-sm hover:border-slate-200 dark:hover:border-white/20"
                                    />
                                </div>
                            </div>

                            <button type="submit" disabled={loading}
                                className="w-full py-5 rounded-2xl bg-slate-900 hover:bg-emerald-600 text-white text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-900/10 hover:shadow-emerald-600/20 transition-all transform active:scale-[0.98] disabled:opacity-50"
                            >
                                {loading ? 'SYNCING DATA...' : 'UPDATE PROFILE →'}
                            </button>
                        </form>
                    </div>

                    {/* ── Change Password Card ── */}
                    <div className="relative bg-white/70 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] border border-white/60 dark:border-white/10 shadow-xl shadow-slate-200/50 dark:shadow-black/50 overflow-hidden transition-all duration-500">
                        <div className="p-8 sm:p-10 border-b border-white/60 dark:border-white/5 bg-white/40 dark:bg-slate-800/20 transition-colors">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                                    <svg fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight">Change Password</h3>
                                    <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-0.5">Update your dispatcher account password</p>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handlePasswordChange} className="p-8 sm:p-10 space-y-6">
                            {pwMessage.text && (
                                <AlertBanner type={pwMessage.type} text={pwMessage.text} onClose={() => setPwMessage({ type: '', text: '' })} />
                            )}

                            <div>
                                <PasswordField
                                    label="Current Password"
                                    value={pwForm.currentPassword}
                                    onChange={e => { setPwForm(p => ({ ...p, currentPassword: e.target.value })); setPwErrors(p => ({ ...p, currentPassword: '' })); }}
                                    placeholder="Enter your current password"
                                />
                                {pwErrors.currentPassword && <p className="mt-2 ml-1 text-[11px] font-bold text-rose-500">{pwErrors.currentPassword}</p>}
                            </div>

                            <div>
                                <PasswordField
                                    label="New Password"
                                    value={pwForm.newPassword}
                                    onChange={e => { setPwForm(p => ({ ...p, newPassword: e.target.value })); setPwErrors(p => ({ ...p, newPassword: '' })); }}
                                    placeholder="Min 8 chars, uppercase, number, symbol"
                                />
                                {pwErrors.newPassword && <p className="mt-2 ml-1 text-[11px] font-bold text-rose-500">{pwErrors.newPassword}</p>}
                                {pwForm.newPassword && (
                                    <div className="mt-3">
                                        <div className="flex gap-1.5 mb-1">
                                            {[1, 2, 3, 4, 5, 6].map(i => (
                                                <div key={i} className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i <= strength.score ? strength.color : 'bg-slate-200 dark:bg-slate-700'}`} />
                                            ))}
                                        </div>
                                        <p className={`text-[10px] font-black uppercase tracking-widest ml-0.5 ${strength.label === 'Weak' ? 'text-rose-500' : strength.label === 'Fair' ? 'text-amber-500' : 'text-emerald-600 dark:text-emerald-400'}`}>
                                            Strength: {strength.label}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div>
                                <PasswordField
                                    label="Confirm New Password"
                                    value={pwForm.confirmPassword}
                                    onChange={e => { setPwForm(p => ({ ...p, confirmPassword: e.target.value })); setPwErrors(p => ({ ...p, confirmPassword: '' })); }}
                                    placeholder="Re-enter your new password"
                                />
                                {pwErrors.confirmPassword && <p className="mt-2 ml-1 text-[11px] font-bold text-rose-500">{pwErrors.confirmPassword}</p>}
                            </div>

                            <div className="flex items-center gap-4 pt-2">
                                <button type="submit" disabled={pwLoading}
                                    className="flex-1 sm:flex-none px-8 py-5 rounded-2xl bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-500 hover:to-blue-500 text-white text-xs font-black uppercase tracking-[0.2em] shadow-xl shadow-indigo-500/20 transition-all transform active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {pwLoading ? (
                                        <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Updating...</>
                                    ) : 'Update Password →'}
                                </button>
                                {(pwForm.currentPassword || pwForm.newPassword || pwForm.confirmPassword) && (
                                    <button
                                        type="button"
                                        onClick={() => { setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' }); setPwErrors({}); }}
                                        className="px-6 py-5 rounded-2xl text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>

                </div>
            </main>
        </DispatcherLayout>
    );
};

export default Settings;
